/**
 * Inline Currency Picker for Booking Modals
 * A compact dropdown that allows users to change currency during checkout
 */

import { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { ChevronDown, Check } from 'lucide-react';

export default function InlineCurrencyPicker({ className = '' }) {
  const { currency, setCurrency, SUPPORTED_CURRENCIES, CURRENCY_CONFIG, loading } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <span className="text-xs text-secondary-text animate-pulse">...</span>
    );
  }

  const currentConfig = CURRENCY_CONFIG[currency] || {};

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-card-border bg-subtle-bg hover:bg-card-bg transition-colors text-secondary-text hover:text-primary-text"
        aria-label="Change currency"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="font-medium">{currency}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-card-bg border border-card-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="p-1.5">
            <div className="text-[10px] font-medium text-secondary-text px-2 py-1 uppercase tracking-wide">
              Currency
            </div>
            {SUPPORTED_CURRENCIES.map((curr) => {
              const config = CURRENCY_CONFIG[curr] || {};
              const isSelected = curr === currency;

              return (
                <button
                  key={curr}
                  type="button"
                  onClick={() => {
                    setCurrency(curr);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary'
                      : 'text-primary-text hover:bg-subtle-bg'
                  }`}
                >
                  <span className="w-8 font-semibold">{curr}</span>
                  <span className="flex-1 text-left text-secondary-text truncate">{config.name}</span>
                  {isSelected && <Check className="w-3 h-3 text-primary flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
